import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  UserFirestoreResponse,
  CreateUserDto,
  UpdateUserDto,
  SyncUserResponseDto,
} = schemas;

// ====== Comparison Schema (not in generated) ======
export const UserComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: UserFirestoreResponse.nullable(),
  firestoreData: z.record(z.unknown()).nullable().optional(),
  matches: z.boolean(),
  differences: z.array(z.object({
    field: z.string(),
    supabaseValue: z.unknown(),
    firestoreValue: z.unknown(),
  })),
  foundInSupabase: z.boolean(),
  foundInFirestore: z.boolean(),
});

// ====== Re-export schemas for external use ======
export const UserResponseSchema = UserFirestoreResponse;
export const CreateUserSchema = CreateUserDto;
export const UpdateUserSchema = UpdateUserDto;

// ====== Type Exports ======
export type UserResponse = z.infer<typeof UserFirestoreResponse>;
export type CreateUser = z.infer<typeof CreateUserDto>;
export type UpdateUser = z.infer<typeof UpdateUserDto>;
export type UserComparisonResult = z.infer<typeof UserComparisonResultSchema>;

/**
 * Create users API factory
 */
export function createUsersApi(client: TypedApiClient) {
  return {
    /**
     * Get all users
     */
    getUsers: (params?: {
      active?: boolean;
    }): Promise<UserResponse[]> => {
      return client.get('/users', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(UserFirestoreResponse),
      });
    },

    /**
     * Get user by ID
     */
    getUserById: (id: string): Promise<UserResponse> => {
      return client.get(apiPath`/users/${id}`, {
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Create a new user
     */
    createUser: (data: CreateUser): Promise<UserResponse> => {
      return client.post('/users', {
        data,
        requestSchema: CreateUserDto,
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Update an existing user
     */
    updateUser: (id: string, data: UpdateUser): Promise<UserResponse> => {
      return client.put(apiPath`/users/${id}`, {
        data,
        requestSchema: UpdateUserDto,
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Enable a user
     */
    enableUser: (id: string): Promise<UserResponse> => {
      return client.patch(apiPath`/users/${id}/enable`, {
        data: {},
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Update user's push token
     */
    updatePushToken: (id: string, pushToken: string): Promise<UserResponse> => {
      return client.put(apiPath`/users/${id}/push-token`, {
        data: { pushToken },
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Remove user's push token
     */
    removePushToken: (id: string): Promise<UserResponse> => {
      return client.patch(apiPath`/users/${id}/push-token/remove`, {
        data: {},
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Update user email (Firebase Auth + Supabase)
     */
    updateEmail: (id: string, email: string): Promise<UserResponse> => {
      return client.patch(apiPath`/users/${id}/email`, {
        data: { email },
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * ATL-1951 — record that a user's password was changed (audit stamp in Supabase).
     *
     * It does NOT change the password and carries none: the `updateUserInAuth` callable sets it,
     * live. This is the change's dual-write mirror, and a mirror can sit in the device outbox
     * (AsyncStorage) and be replayed hours later — so it must hold no credential, and its replay must
     * not be a credential write.
     */
    recordPasswordChange: (id: string): Promise<UserResponse> => {
      return client.patch(apiPath`/users/${id}/password`, {
        data: {},
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * @deprecated ATL-1951 — use `recordPasswordChange`. Kept ONLY because the frozen differential
     * fixture `apps/atlas/global/Firebase/__tests__/fixtures/firebase.master.ts` (byte-pinned to a
     * master blob, so it cannot be edited) still type-checks a call to it. The password argument is
     * DISCARDED, never sent: nothing may put a credential into this request again. Remove with that
     * fixture. (Its own request, not a delegation: the W-writer census reads each member's route
     * from the member itself.)
     */
    updatePassword: (id: string, _discardedPassword: string): Promise<UserResponse> => {
      return client.patch(apiPath`/users/${id}/password`, {
        data: {},
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Soft-delete a user
     */
    softDeleteUser: (id: string): Promise<UserResponse> => {
      return client.delete(apiPath`/users/${id}`, {
        responseSchema: UserFirestoreResponse,
      });
    },

    /**
     * Compare user with Firestore data
     */
    compareWithFirestore: (firestoreId: string): Promise<UserComparisonResult> => {
      return client.get(apiPath`/users/compare/${firestoreId}`, {
        responseSchema: UserComparisonResultSchema,
      });
    },

    /**
     * Sync the caller's OWN Supabase user row from the authoritative Firestore users/{uid} doc.
     * Used after the Misc-screen test-org chooser switches orgs (ATL-545) so Supabase follows
     * Firestore. Auth is the bearer token (no body); the backend reads the token's own uid.
     */
    syncSelfFromFirestore: (): Promise<z.infer<typeof SyncUserResponseDto>> => {
      return client.post('/users/me/sync-from-firestore', {
        data: {},
        responseSchema: SyncUserResponseDto,
      });
    },
  };
}
